import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { SESSION_STORAGE_KEYS } from "@dw/constants";
import { getQuerySettings, QueryKeys } from '@dw/models/shared';
import { cleanJsonResponse } from '@dw/utils';
import { isDayjs } from '@dw/dayjs';

type UpdaterFn<T> = (currentValue: T | null) => T;
type Updater<T> = UpdaterFn<T> | T | undefined;

const isUpdaterFn = <T,>(u: Updater<T>): u is UpdaterFn<T> =>
  typeof u === "function";

export function useSessionStorage<T>(storageKey: SESSION_STORAGE_KEYS, seedValue?: T) {

  const queryClient = useQueryClient();
  const querySettings = useMemo(() => getQuerySettings(), []);

  const initialized = useRef(false);

  const getItemFromSessionStorage = useCallback((key: SESSION_STORAGE_KEYS): T => {
    const sessionItem = sessionStorage.getItem(key);
    if (sessionItem && (sessionItem.startsWith('{') || sessionItem.startsWith('['))) {
      return cleanJsonResponse(JSON.parse(sessionItem)) as T;
    } else {
      return cleanJsonResponse(sessionItem) as T;
    }
  }, []);

  const cachedItem = useQuery<T | null>({
    ...querySettings,
    queryKey: [QueryKeys.SessionStorage, storageKey],
    queryFn: () => {
      return new Promise((resolve) => {
        resolve(getItemFromSessionStorage(storageKey));
      });
    },
  });

  const invalidateQueryCache = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.length === 2 && query.queryKey[0] === QueryKeys.SessionStorage && query.queryKey[1] === storageKey
    });
  }, [queryClient, storageKey]);

  const updateItem = useCallback((updater: Updater<T>, isInitUpdate?: boolean) => {
    const updatedItem = isUpdaterFn(updater)
      ? updater(getItemFromSessionStorage(storageKey) ?? null)
      : updater;

    const sessionItem = isDayjs(updatedItem)
      ? updatedItem.toISOString()
      : JSON.stringify(updatedItem);
      
    sessionStorage.setItem(storageKey, sessionItem);
    if (isInitUpdate) {
      setTimeout(() => {
        invalidateQueryCache();
      })
    } else {
      invalidateQueryCache();
    }
  }, [invalidateQueryCache, storageKey, getItemFromSessionStorage]);

  const deleteItem = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    invalidateQueryCache();
  }, [invalidateQueryCache, storageKey]);

  useEffect(() => {
    if (!initialized.current && seedValue) {
      initialized.current = true;
      updateItem(seedValue, true);
    }
  }, [seedValue, updateItem]);

  return useMemo(() => ({
    value: cachedItem.data ?? null,
    updateItem,
    deleteItem,
    initialized: initialized && cachedItem.isSuccess
  }), [cachedItem, updateItem, deleteItem, initialized]);
}